import { AxiosResponse, InternalAxiosRequestConfig } from './types'
import { ElMessage } from 'element-plus'
import qs from 'qs'
import { SUCCESS_CODE, TRANSFORM_REQUEST_DATA } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { objToFormData } from '@/utils'
import { handleAuthFailure } from '@/utils/authFailure'
import { isBusinessLoginExpired } from '@/utils/tokenRefresh'

const defaultRequestInterceptors = (config: InternalAxiosRequestConfig) => {
  if (
    config.method === 'post' &&
    config.headers['Content-Type'] === 'application/x-www-form-urlencoded'
  ) {
    config.data = qs.stringify(config.data)
  } else if (
    TRANSFORM_REQUEST_DATA &&
    config.method === 'post' &&
    config.headers['Content-Type'] === 'multipart/form-data' &&
    !(config.data instanceof FormData)
  ) {
    config.data = objToFormData(config.data)
  }
  if (config.method === 'get' && config.params) {
    let url = config.url as string
    url += '?'
    const keys = Object.keys(config.params)
    const append = (k: string, val: unknown) => {
      url += `${encodeURIComponent(k)}=${encodeURIComponent(String(val))}&`
    }
    for (const key of keys) {
      const v = config.params[key]
      if (v === undefined || v === null || v === '') {
        continue
      }
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item === undefined || item === null || item === '') {
            continue
          }
          append(key, item)
        }
      } else if (typeof v === 'object') {
        // 避免 [object Object] 导致服务端绑定/反序列化异常
        continue
      } else {
        append(key, v)
      }
    }
    url = url.substring(0, url.length - 1)
    config.params = {}
    config.url = url
  }
  return config
}
// new
const defaultResponseInterceptors = (response: AxiosResponse) => {
  // 检查响应头中是否有新的访问令牌
  if (response.headers && response.headers['new-access-token']) {
    const newAccessToken = response.headers['new-access-token']
    if (newAccessToken) {
      const extractedToken = newAccessToken.substring(7) // 移除 "Bearer " 前缀
      // 更新本地存储中的token
      localStorage.setItem('accessToken', extractedToken)
      // 更新状态管理中的token
      import('@/store/modules/user')
        .then(({ useUserStoreWithOut }) => {
          const userStore = useUserStoreWithOut()
          userStore.setToken(extractedToken)
        })
        .catch((error) => {
          console.error('Failed to update token in store:', error)
          // 如果状态管理更新失败，至少保证本地存储被更新
          localStorage.setItem('accessToken', extractedToken)
        })
    }
  }

  if (response?.config?.responseType === 'blob') {
    const blob = response.data as Blob
    const contentType = String(response.headers?.['content-type'] || '').toLowerCase()
    // Excel/二进制导出直接透传，禁止对原始 Blob 调用 text()（浏览器中 Blob 一次性读取，会导致下载空文件）
    if (
      contentType.includes('spreadsheetml') ||
      contentType.includes('ms-excel') ||
      contentType.includes('octet-stream')
    ) {
      return response
    }

    const silentError = (response.config as InternalAxiosRequestConfig & { silentError?: boolean })
      ?.silentError

    const handleJsonBlob = (text: string) => {
      try {
        const jsonData = JSON.parse(text)
        if (jsonData.code !== SUCCESS_CODE) {
          const errorMessage = jsonData?.message || `请求失败，状态码: ${jsonData.code}`
          if (!silentError) {
            ElMessage.error(errorMessage)
          }

          if (isBusinessLoginExpired(jsonData)) {
            setTimeout(() => {
              handleAuthFailure()
            }, 1500)

            return {
              ...jsonData,
              _isLoginExpired: true
            }
          }

          return {
            ...jsonData,
            _isError: true
          }
        }
        if (jsonData.code === 0 && jsonData.message) {
          ElMessage.success(jsonData.message)
        }
        return response
      } catch {
        return response
      }
    }

    // 仅探测 JSON 错误体：用 slice 避免破坏原始 Blob
    if (!blob || blob.size === 0) {
      return response
    }
    const peekSize = Math.min(blob.size, 64)
    return blob
      .slice(0, peekSize)
      .text()
      .then((head: string) => {
        if (!head.trimStart().startsWith('{')) {
          return response
        }
        return blob.text().then(handleJsonBlob)
      })
      .catch((error) => Promise.reject(error))
  } else {
    const responseData = response.data
    // 检查HTTP状态码的登录失效情况
    const status = response.status
    const isHttpLoginExpired = status === 401

    // 如果检测到任何形式的登录失效
    if (isHttpLoginExpired || isBusinessLoginExpired(responseData)) {
      // 延迟一下再跳转，让用户看到提示信息
      setTimeout(() => {
        handleAuthFailure()
      }, 1500)

      // 返回特殊标记而不是抛出错误
      return {
        ...responseData,
        _isLoginExpired: true
      }
    }

    // 如果状态码不为SUCCESS_CODE且不是登录失效，显示错误信息
    // if (responseData.code !== SUCCESS_CODE && !responseData._isLoginExpired) {
    //   const errorMessage = responseData?.message || `请求失败，状态码: ${responseData.code}`
    //   ElMessage.error(errorMessage)
    // }
    // 如果状态码不为SUCCESS_CODE且不是登录失效，显示错误信息
    if (responseData.code !== SUCCESS_CODE && !responseData._isLoginExpired) {
      const silentError = (response.config as InternalAxiosRequestConfig & { silentError?: boolean })
        ?.silentError
      // 检查message是否包含Success，如果是则不显示错误信息
      const isSuccessfulMessage =
        responseData?.message &&
        typeof responseData.message === 'string' &&
        responseData.message.toLowerCase().includes('success')
      if (!silentError && !isSuccessfulMessage && responseData?.status != 0) {
        const errorMessage = responseData?.message || `请求失败，状态码: ${responseData.code}`
        // 如果错误码是-1，显示"数据同步中"，否则显示原始错误消息
        // const displayMessage = responseData.code === -1 ? '数据同步中' : errorMessage
        ElMessage.error(errorMessage)
      }
    }

    // 直接返回响应数据
    return responseData
  }
}

// 错误响应拦截器 - 处理HTTP状态码错误
const defaultResponseErrorInterceptor = (error: any) => {
  if (error.response) {
    const { status, data, config } = error.response
    console.log('status,当前请求状态', status, error.response)

    // 401：登录失效（由 service.ts / isBusinessLoginExpired 处理）；403：权限不足（≠掉线，勿踢登录）
    if (status === 401 || status === 403) {
      return Promise.reject(error)
    }

    // 对于其他错误，可以自定义处理
    const errorMessage = data?.message || `请求失败，状态码: ${status}`

    // 避免重复显示错误消息
    if (!config._errorShown) {
      config._errorShown = true
      // 根据需要决定是否显示错误消息
      // ElMessage.error(errorMessage);
    }
  } else if (error.request) {
    // 网络错误
    if (error.code !== 'ERR_CANCELED') {
      // 忽略取消请求的错误
      ElMessage.error('网络错误，请检查网络连接')
    }
  } else {
    // 其他错误
    ElMessage.error(error.message || '请求配置错误')
  }

  return Promise.reject(error)
}
export { defaultResponseInterceptors, defaultRequestInterceptors, defaultResponseErrorInterceptor }
